import { Entity, RELATION_PART_OF } from '@backstage/catalog-model';

/**
 * Extracts the parent component name from an entity's relations.
 * Looks for RELATION_PART_OF relation to find the parent component.
 * 
 * @param entity - The entity to extract parent from
 * @returns The parent component name (extracted from targetRef) or null if not found
 */
export const extractParentComponentName = (entity: Entity | null | undefined): string | null => {
  if (!entity?.relations) {
    return null;
  }

  // Find RELATION_PART_OF relation to get parent component
  const partOfRelation = entity.relations.find(
    (rel) => rel.type === RELATION_PART_OF || rel.type === 'partOf'
  );

  if (!partOfRelation?.targetRef) {
    return null;
  }

  // Extract parent name from targetRef (format: "component:default/parent-name")
  const targetParts = partOfRelation.targetRef.split('/');
  if (targetParts.length > 0) {
    return targetParts[targetParts.length - 1];
  }

  return null;
};

/**
 * Fetches the full entity details including relations from the catalog API.
 * This is a fallback when relations are not included in getEntities response.
 * 
 * @param catalogApi - The catalog API instance (from catalogApiRef)
 * @param entityName - The name of the entity to fetch
 * @returns The full entity with relations, or null if not found or method unavailable
 */
export const fetchEntityWithRelations = async (
  catalogApi: any,
  entityName: string
): Promise<Entity | null> => {
  try {
    const entityRef = `component:default/${entityName.toLowerCase()}`;
    
    // Type assertion to handle potential missing method
    // getEntityByRef may not be available on all catalogApi implementations
    if (catalogApi?.getEntityByRef) {
      return await catalogApi.getEntityByRef(entityRef);
    }
  } catch (error) {
    console.warn('Unable to fetch entity with relations:', error);
  }
  
  return null;
};

/**
 * Gets the parent component name for an entity.
 * First checks if relations are in the entity, otherwise fetches full entity details.
 * 
 * @param entity - The entity to get parent for
 * @param catalogApi - The catalog API instance (optional, used for fallback)
 * @param entityName - The name of the entity (optional, used for fallback fetch)
 * @returns The parent component name or null if not found
 */
export const getParentComponentName = async (
  entity: Entity | null | undefined,
  catalogApi?: any,
  entityName?: string
): Promise<string | null> => {
  // First, try to extract from entity relations if available
  const parentFromRelations = extractParentComponentName(entity);
  if (parentFromRelations) {
    return parentFromRelations;
  }

  // If relations not available and we have catalogApi and entityName, try to fetch full entity
  if (catalogApi && entityName) {
    const fullEntity = await fetchEntityWithRelations(catalogApi, entityName);
    return extractParentComponentName(fullEntity);
  }

  return null;
};

/**
 * Checks if an entity belongs to a specific parent project/component.
 * 
 * @param entity - The entity to check
 * @param parentName - The name of the parent project/component to check against
 * @param catalogApi - The catalog API instance (optional, used for fallback)
 * @param entityName - The name of the entity (optional, used for fallback fetch)
 * @returns true if the entity belongs to the specified parent, false otherwise
 */
export const belongsToParent = async (
  entity: Entity | null | undefined,
  parentName: string,
  catalogApi?: any,
  entityName?: string
): Promise<boolean> => {
  if (!entity || !parentName) {
    return false;
  }

  const parentComponentName = await getParentComponentName(entity, catalogApi, entityName);
  
  if (!parentComponentName) {
    return false;
  }

  // Compare parent name with provided parent name (case-insensitive)
  return parentComponentName.toLowerCase() === parentName.toLowerCase();
};

